import React from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import type {
  PerformanceSchedule,
  PerformanceSchedulePayload,
} from '../../types/performance.types';

interface ScheduleFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  schedule?: PerformanceSchedule | null;
  onSubmit: (payload: PerformanceSchedulePayload) => Promise<void>;
}

const currentFy = () => {
  const now = new Date();
  const year = now.getMonth() >= 6 ? now.getFullYear() : now.getFullYear() - 1;
  return {
    start: `${year}-07-01`,
    end: `${year + 1}-06-30`,
    goalOpen: `${year}-07-01`,
    goalClose: `${year}-08-31`,
    midStart: `${year + 1}-01-05`,
    midEnd: `${year + 1}-01-30`,
    eoyStart: `${year + 1}-06-01`,
    eoyEnd: `${year + 1}-06-30`,
    name: `FY${year}–${String(year + 1).slice(2)} Performance Cycle`,
  };
};

const defaultPayload = (): PerformanceSchedulePayload => {
  const dates = currentFy();
  return {
    name: dates.name,
    startDate: dates.start,
    endDate: dates.end,
    goalOpenDate: dates.goalOpen,
    goalCloseDate: dates.goalClose,
    minGoals: 3,
    maxGoals: 8,
    minWeight: 5,
    maxWeight: 60,
    midEnabled: true,
    midStart: dates.midStart,
    midEnd: dates.midEnd,
    midSelfStart: dates.midStart,
    midSelfEnd: `${dates.midStart.slice(0, 8)}20`,
    midManagerStart: `${dates.midStart.slice(0, 8)}15`,
    midManagerEnd: dates.midEnd,
    eoyEnabled: true,
    eoyStart: dates.eoyStart,
    eoyEnd: dates.eoyEnd,
    eoySelfStart: dates.eoyStart,
    eoySelfEnd: `${dates.eoyStart.slice(0, 8)}19`,
    eoyManagerStart: `${dates.eoyStart.slice(0, 8)}08`,
    eoyManagerEnd: dates.eoyEnd,
  };
};

const fromSchedule = (schedule: PerformanceSchedule): PerformanceSchedulePayload => ({
  name: schedule.name,
  startDate: schedule.startDate,
  endDate: schedule.endDate,
  goalOpenDate: schedule.goalOpenDate,
  goalCloseDate: schedule.goalCloseDate,
  minGoals: schedule.minGoals,
  maxGoals: schedule.maxGoals,
  minWeight: schedule.minWeight,
  maxWeight: schedule.maxWeight,
  midEnabled: schedule.mid.enabled,
  midStart: schedule.mid.start,
  midEnd: schedule.mid.end,
  midSelfStart: schedule.mid.selfStart,
  midSelfEnd: schedule.mid.selfEnd,
  midManagerStart: schedule.mid.managerStart,
  midManagerEnd: schedule.mid.managerEnd,
  eoyEnabled: schedule.eoy.enabled,
  eoyStart: schedule.eoy.start,
  eoyEnd: schedule.eoy.end,
  eoySelfStart: schedule.eoy.selfStart,
  eoySelfEnd: schedule.eoy.selfEnd,
  eoyManagerStart: schedule.eoy.managerStart,
  eoyManagerEnd: schedule.eoy.managerEnd,
});

export const ScheduleFormDialog: React.FC<ScheduleFormDialogProps> = ({
  open,
  onOpenChange,
  schedule,
  onSubmit,
}) => {
  const [form, setForm] = React.useState<PerformanceSchedulePayload>(defaultPayload);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) return;
    setForm(schedule ? fromSchedule(schedule) : defaultPayload());
    setError(null);
  }, [open, schedule]);

  const set = <K extends keyof PerformanceSchedulePayload>(
    key: K,
    value: PerformanceSchedulePayload[K]
  ) => setForm((current) => ({ ...current, [key]: value }));

  const dateField = (
    label: string,
    key: keyof PerformanceSchedulePayload,
    min?: string,
    max?: string
  ) => (
    <div className="space-y-1.5">
      <Label htmlFor={`schedule-${String(key)}`}>{label}</Label>
      <Input
        id={`schedule-${String(key)}`}
        type="date"
        value={(form[key] as string) || ''}
        min={min}
        max={max}
        onChange={(event) => set(key, event.target.value as never)}
      />
    </div>
  );

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSubmit(form);
      onOpenChange(false);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Could not save the schedule.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] sm:max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{schedule ? 'Edit schedule' : 'New schedule'}</DialogTitle>
          <DialogDescription>
            {schedule
              ? 'Dates and windows can be changed while the schedule is not closed.'
              : 'New schedules start as Draft. Employees cannot see them until you activate.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="schedule-name">Name</Label>
            <Input
              id="schedule-name"
              value={form.name}
              onChange={(event) => set('name', event.target.value)}
            />
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            {dateField('Cycle starts', 'startDate')}
            {dateField('Cycle ends', 'endDate')}
            {dateField('Goal setting opens', 'goalOpenDate', form.startDate, form.endDate)}
            {dateField('Goal setting closes', 'goalCloseDate', form.goalOpenDate, form.endDate)}
          </div>

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {(
              [
                ['minGoals', 'Min goals'],
                ['maxGoals', 'Max goals'],
                ['minWeight', 'Min weight %'],
                ['maxWeight', 'Max weight %'],
              ] as [keyof PerformanceSchedulePayload, string][]
            ).map(([key, label]) => (
              <div key={String(key)} className="space-y-1.5">
                <Label htmlFor={`schedule-${String(key)}`}>{label}</Label>
                <Input
                  id={`schedule-${String(key)}`}
                  type="number"
                  value={String(form[key])}
                  onChange={(event) => set(key, Number(event.target.value) as never)}
                />
              </div>
            ))}
          </div>

          <section className="rounded-xl border border-border p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold">Mid-year review</p>
                <p className="text-xs text-muted-foreground">
                  Switching it off removes the tab for everyone.
                </p>
              </div>
              <Switch
                checked={form.midEnabled}
                onCheckedChange={(checked) => set('midEnabled', checked)}
              />
            </div>
            {form.midEnabled ? (
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                {dateField('Review opens', 'midStart', form.startDate, form.endDate)}
                {dateField('Review closes', 'midEnd', form.midStart || form.startDate, form.endDate)}
                {dateField('Self-review starts', 'midSelfStart', form.midStart || form.startDate, form.midEnd || form.endDate)}
                {dateField('Self-review ends', 'midSelfEnd', form.midSelfStart || form.midStart || form.startDate, form.midEnd || form.endDate)}
                {dateField('Manager review starts', 'midManagerStart', form.midStart || form.startDate, form.midEnd || form.endDate)}
                {dateField('Manager review ends', 'midManagerEnd', form.midManagerStart || form.midStart || form.startDate, form.midEnd || form.endDate)}
              </div>
            ) : null}
          </section>

          <section className="rounded-xl border border-border p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold">End-of-year review</p>
                <p className="text-xs text-muted-foreground">
                  Calibration starts after this window and results are released on Close.
                </p>
              </div>
              <Switch
                checked={form.eoyEnabled}
                onCheckedChange={(checked) => set('eoyEnabled', checked)}
              />
            </div>
            {form.eoyEnabled ? (
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                {dateField('Review opens', 'eoyStart', form.startDate, form.endDate)}
                {dateField('Review closes', 'eoyEnd', form.eoyStart || form.startDate, form.endDate)}
                {dateField('Self-review starts', 'eoySelfStart', form.eoyStart || form.startDate, form.eoyEnd || form.endDate)}
                {dateField('Self-review ends', 'eoySelfEnd', form.eoySelfStart || form.eoyStart || form.startDate, form.eoyEnd || form.endDate)}
                {dateField('Manager review starts', 'eoyManagerStart', form.eoyStart || form.startDate, form.eoyEnd || form.endDate)}
                {dateField('Manager review ends', 'eoyManagerEnd', form.eoyManagerStart || form.eoyStart || form.startDate, form.eoyEnd || form.endDate)}
              </div>
            ) : null}
          </section>

          {error ? (
            <p className="rounded-[10px] border border-destructive-border bg-destructive-bg px-3 py-2 text-sm text-destructive">
              {error}
            </p>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={saving}>
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            {schedule ? 'Save changes' : 'Create schedule'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
